/**
=========================================================
* Material Dashboard 2 React - Newsletter Management
=========================================================
*/

import { useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSnackbar from "components/MDSnackbar";
import RichTextEditor from "components/RichTextEditor";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import RecordTable from "components/RecordTable";

import { adminApi } from "services/api";
import { newsletterSubscribersToRows } from "utils/exportUtils";
import { sendResultNotice, hasDraft, newsletterFailureNotice } from "./helpers";

const SUBSCRIBED_OPTIONS = [
  { value: "true", label: "Subscribed" },
  { value: "false", label: "Unsubscribed" },
];

const EXPORT_CONFIG = {
  fetchAll: adminApi.exportNewsletterSubscribers,
  toRows: newsletterSubscribersToRows,
  filename: "newsletter-subscribers",
  title: "Newsletter Subscribers",
};

// Below md the table becomes stacked cards: give the delete button a ~40px tap target there.
const ROW_ACTION_SX = { width: { xs: 40, md: "auto" }, height: { xs: 40, md: "auto" } };

function Newsletter() {
  const [stats, setStats] = useState({ total: 0, subscribed: 0, unsubscribed: 0 });
  const [loading, setLoading] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailContent, setEmailContent] = useState("");
  const [sending, setSending] = useState(false);
  const theme = useTheme();
  // Below sm the composer goes full screen (RecordTable turns the subscriber rows into cards below md).
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    color: "success",
    icon: "check",
    title: "",
    content: "",
  });

  const closeSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const showSnackbar = (color, icon, title, content) => {
    setSnackbar({ open: true, color, icon, title, content });
  };

  const handleDelete = async (id, email) => {
    if (window.confirm(`Are you sure you want to delete subscriber: ${email}?`)) {
      try {
        await adminApi.deleteNewsletterSubscriber(id);
        showSnackbar("success", "check", "Success", "Subscriber deleted successfully");
        setReloadToken((t) => t + 1);
      } catch (error) {
        console.error("Error deleting subscriber:", error);
        showSnackbar("error", "error", "Error", "Failed to delete subscriber");
      }
    }
  };

  const handleOpenSendDialog = () => {
    setSendDialogOpen(true);
    setEmailSubject("");
    setEmailContent("");
  };

  const closeComposer = () => {
    setSendDialogOpen(false);
    setEmailSubject("");
    setEmailContent("");
  };

  // Backdrop click, Esc and Cancel all land here: never close mid-send, and ask before throwing
  // away a draft (it used to vanish on a stray click outside the dialog).
  const requestCloseComposer = () => {
    if (sending) return;
    if (
      hasDraft(emailSubject, emailContent) &&
      !window.confirm(
        "Discard this newsletter? The subject and content you've written will be lost."
      )
    ) {
      return;
    }
    closeComposer();
  };

  const handleSendNewsletter = async () => {
    if (!emailSubject.trim() || !emailContent.trim()) {
      showSnackbar("warning", "warning", "Warning", "Please fill in both subject and content");
      return;
    }

    if (stats.subscribed === 0) {
      showSnackbar("warning", "warning", "Warning", "No active subscribers to send to");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to send this newsletter to ${stats.subscribed} subscribers?`
      )
    ) {
      return;
    }

    try {
      setSending(true);
      const response = await adminApi.sendNewsletter({
        subject: emailSubject,
        htmlContent: emailContent,
      });

      // The real sent/failed counts — never "sent" when nothing went out.
      const notice = sendResultNotice(response);
      showSnackbar(notice.color, notice.icon, notice.title, notice.content);
      // Some went out, so the draft is done with: sending it again would repeat them.
      closeComposer();
    } catch (error) {
      console.error("Error sending newsletter:", error);
      // Keep the composer open with the draft. With no reply the send may still be running:
      // say so (the server refuses the same newsletter again for a day).
      const notice = newsletterFailureNotice(error);
      showSnackbar(notice.color, notice.icon, notice.title, notice.content);
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns = [
    { Header: "email", accessor: "email", align: "left" },
    { Header: "status", accessor: "status", align: "center" },
    { Header: "subscribed", accessor: "subscribedAt", align: "left" },
    { Header: "actions", accessor: "actions", align: "center" },
  ];

  const renderRow = (subscriber) => ({
    email: (
      <MDTypography variant="button" fontWeight="regular">
        {subscriber.email}
      </MDTypography>
    ),
    status: (
      <Chip
        label={subscriber.subscribed ? "Active" : "Unsubscribed"}
        color={subscriber.subscribed ? "success" : "default"}
        size="small"
      />
    ),
    subscribedAt: (
      <MDTypography variant="button" fontWeight="regular">
        {formatDate(subscriber.subscribedAt)}
      </MDTypography>
    ),
    actions: (
      <Tooltip title="Delete">
        <IconButton
          color="error"
          size="small"
          sx={ROW_ACTION_SX}
          aria-label={`Delete ${subscriber.email}`}
          onClick={() => handleDelete(subscriber._id, subscriber.email)}
        >
          <Icon>delete</Icon>
        </IconButton>
      </Tooltip>
    ),
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          {/* Statistics Cards */}
          <Grid item xs={12} md={4}>
            <Card>
              <MDBox
                p={{ xs: 2, md: 3 }}
                textAlign={{ xs: "left", md: "center" }}
                display={{ xs: "flex", md: "block" }}
                alignItems="center"
                gap={2}
              >
                <MDBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width={{ xs: "3rem", md: "4rem" }}
                  height={{ xs: "3rem", md: "4rem" }}
                  flexShrink={0}
                  borderRadius="lg"
                  shadow="md"
                  color="white"
                  bgColor="info"
                  mx={{ xs: 0, md: "auto" }}
                  mb={{ xs: 0, md: 2 }}
                >
                  <Icon fontSize="large">groups</Icon>
                </MDBox>
                <MDBox>
                  <MDTypography variant="h5" fontWeight="medium">
                    {loading ? <CircularProgress size={20} /> : stats.total}
                  </MDTypography>
                  <MDTypography variant="button" color="text" fontWeight="regular">
                    Total Subscribers
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <MDBox
                p={{ xs: 2, md: 3 }}
                textAlign={{ xs: "left", md: "center" }}
                display={{ xs: "flex", md: "block" }}
                alignItems="center"
                gap={2}
              >
                <MDBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width={{ xs: "3rem", md: "4rem" }}
                  height={{ xs: "3rem", md: "4rem" }}
                  flexShrink={0}
                  borderRadius="lg"
                  shadow="md"
                  color="white"
                  bgColor="success"
                  mx={{ xs: 0, md: "auto" }}
                  mb={{ xs: 0, md: 2 }}
                >
                  <Icon fontSize="large">check_circle</Icon>
                </MDBox>
                <MDBox>
                  <MDTypography variant="h5" fontWeight="medium">
                    {loading ? <CircularProgress size={20} /> : stats.subscribed}
                  </MDTypography>
                  <MDTypography variant="button" color="text" fontWeight="regular">
                    Active Subscribers
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <MDBox
                p={{ xs: 2, md: 3 }}
                textAlign={{ xs: "left", md: "center" }}
                display={{ xs: "flex", md: "block" }}
                alignItems="center"
                gap={2}
              >
                <MDBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width={{ xs: "3rem", md: "4rem" }}
                  height={{ xs: "3rem", md: "4rem" }}
                  flexShrink={0}
                  borderRadius="lg"
                  shadow="md"
                  color="white"
                  bgColor="error"
                  mx={{ xs: 0, md: "auto" }}
                  mb={{ xs: 0, md: 2 }}
                >
                  <Icon fontSize="large">cancel</Icon>
                </MDBox>
                <MDBox>
                  <MDTypography variant="h5" fontWeight="medium">
                    {loading ? <CircularProgress size={20} /> : stats.unsubscribed}
                  </MDTypography>
                  <MDTypography variant="button" color="text" fontWeight="regular">
                    Unsubscribed
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          {/* Subscribers List */}
          <Grid item xs={12} mt={{ xs: 2, md: 0 }}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexWrap="wrap"
                gap={1.5}
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Newsletter Subscribers
                </MDTypography>
                <MDButton
                  variant="contained"
                  color="white"
                  size="small"
                  onClick={handleOpenSendDialog}
                  disabled={stats.subscribed === 0}
                  sx={({ breakpoints }) => ({ [breakpoints.down("md")]: { minHeight: 36 } })}
                >
                  <Icon sx={{ mr: 1 }}>send</Icon>
                  Send Newsletter
                </MDButton>
              </MDBox>
              <RecordTable
                columns={columns}
                fetchPage={adminApi.getNewsletterSubscribers}
                renderRow={renderRow}
                statusOptions={SUBSCRIBED_OPTIONS}
                reloadToken={reloadToken}
                exportConfig={EXPORT_CONFIG}
                onLoaded={(res) => {
                  if (res.stats) setStats(res.stats);
                  setLoading(false);
                }}
                emptyMessage="No subscribers yet"
              />
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Send Newsletter Dialog */}
      <Dialog
        open={sendDialogOpen}
        onClose={requestCloseComposer}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle>
          <MDTypography variant="h5" fontWeight="medium">
            Send Newsletter
          </MDTypography>
          <MDTypography variant="button" color="text" fontWeight="regular">
            Compose and send email to {stats.subscribed} active subscribers
          </MDTypography>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox mb={3}>
            <MDInput
              type="text"
              label="Email Subject"
              fullWidth
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              required
            />
          </MDBox>
          <MDBox>
            <RichTextEditor
              value={emailContent}
              onChange={setEmailContent}
              label="Email Content"
              height={400}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={requestCloseComposer} disabled={sending}>
            Cancel
          </Button>
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleSendNewsletter}
            disabled={sending || !emailSubject.trim() || !emailContent.trim()}
          >
            {sending ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                Sending...
              </>
            ) : (
              <>
                <Icon sx={{ mr: 1 }}>send</Icon>
                Send to {stats.subscribed} Subscribers
              </>
            )}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <MDSnackbar
        color={snackbar.color}
        icon={snackbar.icon}
        title={snackbar.title}
        content={snackbar.content}
        open={snackbar.open}
        onClose={closeSnackbar}
        close={closeSnackbar}
        bgWhite
      />

      <Footer />
    </DashboardLayout>
  );
}

export default Newsletter;
